import { userInfoAtom } from 'app/atoms';
import { Version } from 'app/components/version';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useDeviceWidth } from 'app/hooks/use-device-size';
import { getActiveUnavailabilityRange } from 'app/lib/unavailability';
import { useMyProfile } from 'app/network/profile';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import type * as profile from 'app/screens/profile/navigator';
import type * as root from 'app/screens/root-stack';
import type { NavigationScreen } from 'app/types/navigation';
import { usePageTitle, useUISize } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai/index';
import { useEffect, type ComponentProps, type FC } from 'react';
import { RefreshControl } from 'react-native';
import LogoutButton from 'app/components/profile/logout-button';
import { ButtonEditProfileAvatar } from 'app/components/profile/my-information/avatar-section';
import { MyInformations } from 'app/components/profile/my-informations';
import { ProfileLinks } from 'app/components/profile/profile-links';
import { ProfileShortInfo } from 'app/components/profile/profile-short-info';
import Responsive from 'app/components/responsive';
import { IconEye, IconPencil } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { ProfileOverviewHeaderTitle } from './profile-overview-header-title';
import { UnavailableButton } from './unavailable-button';
import { UnavailabilityStatus } from './unavailability-status';
import { useHasPartagePlusSubscription } from 'app/screens/billboard/landing-page/shared';
import { UnlockFullExperienceCard } from 'app/screens/billboard/landing-page/components/unlock-full-experience-card';

type ProfileOverviewScreenProps = ComponentProps<NavigationScreen<'profile-overview'>> & { isDesktop: boolean };

function renderProfileOverviewHeaderTitle() {
  return <ProfileOverviewHeaderTitle />;
}

export const ProfileOverviewScreen: FC<ProfileOverviewScreenProps> = ({ navigation }) => {
  const { t } = useScopedTranslation();
  const { isSmallUI } = useUISize();
  const hasPartagePlusSubscription = useHasPartagePlusSubscription();

  useNavigationOptions(navigation, {
    headerTitle: renderProfileOverviewHeaderTitle,
  });

  const setPageTitle = usePageTitle('profile-overview');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const userInfo = useAtomValue(userInfoAtom);
  const { isRefetching, refetch, data: profile } = useMyProfile();

  const activeUnavailabilityRange = getActiveUnavailabilityRange(profile?.unavailability);

  const plannedUnavailabilityRange = (() => {
    const range = profile?.unavailability?.find((r) => new Date(r.start) > new Date());
    return range ? { start: new Date(range.start), end: new Date(range.end) } : null;
  })();

  const navigate = (route: keyof root.Parameters | keyof profile.Parameters, screen?: string, params?: object) =>
    // @ts-expect-error tuple union thing?
    navigation.navigate(route, { screen, ...(params || {}) }, { pop: true });

  const isZoomedDisplay = useDeviceWidth() <= 320;
  return (
    <Page refreshControl={<RefreshControl onRefresh={() => void refetch()} refreshing={isRefetching} />}>
      <ProfileShortInfo />

      <Row gap="spacing.general.x2" justifyContent="space-evenly" marginTop="spacing.general.x4">
        {isSmallUI ? (
          <Button
            onPress={() => navigate('my-information')}
            style={{ height: 40, flexGrow: 1, flexBasis: 0 }}
            variant="secondaryLight"
          >
            <Row gap="spacing.general.x1">
              <Text lineHeight={24} style={{ fontSize: isZoomedDisplay ? 12 : 16 }}>
                {t('editProfileButtonLabel')}
              </Text>
              <IconPencil strokeWidth={ICON_STROKE_WIDTH} size={24} />
            </Row>
          </Button>
        ) : (
          <ButtonEditProfileAvatar profile={profile} />
        )}

        {userInfo ? (
          <Button
            onPress={() => navigateToUserProfile(navigation, userInfo.id)}
            style={{ height: 40, flexGrow: 1, flexBasis: 0 }}
            variant="secondaryLight"
          >
            <Row gap="spacing.general.x1">
              <Text lineHeight={24} style={{ fontSize: isZoomedDisplay ? 12 : 16 }}>
                {t('myProfileButtonLabel')}
              </Text>
              <IconEye strokeWidth={ICON_STROKE_WIDTH} size={24} />
            </Row>
          </Button>
        ) : null}
      </Row>

      {activeUnavailabilityRange ? (
        <UnavailabilityStatus
          startDate={activeUnavailabilityRange.start.toISOString()}
          endDate={activeUnavailabilityRange.end.toISOString()}
          onDeleted={() => void refetch()}
        />
      ) : plannedUnavailabilityRange ? (
        <UnavailabilityStatus
          planned
          startDate={plannedUnavailabilityRange.start.toISOString()}
          endDate={plannedUnavailabilityRange.end.toISOString()}
          onDeleted={() => void refetch()}
        />
      ) : (
        <UnavailableButton />
      )}

      {hasPartagePlusSubscription === false ? (
        <Column marginTop="spacing.general.x4">
          <UnlockFullExperienceCard onPress={() => navigate('payment')} variant="small" />
        </Column>
      ) : null}
      <Responsive
        desktop={<MyInformations />}
        mobile={
          <>
            <ProfileLinks />
            <LogoutButton />
          </>
        }
      />
      <Column>
        <Version />
      </Column>
    </Page>
  );
};
